/**
 * Pulsante header «Avvia Scansione Vulnerabilità» (icona mano = operazione forzata a mano).
 *
 * Avvia il flow Prefect manual-vuln-scan (POST /admin/prefect/manual-scan): rianalisi dei
 * bundle attivi con attesa della fine, poi findings-snapshot → triage-persistence →
 * product-triage-autocreate → notify-new-findings in sequenza. Sempre visibile, come "Esegui
 * ora" su /jobs: chi non è admin riceve il popup di permesso negato. Mentre un run è attivo il
 * pulsante diventa un link a /jobs (Scansione in corso).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/index.js';
import { useManualScanStatus, useStartManualScan } from './hooks.js';
import { RunDenyPopup } from './RunDenyPopup.js';
import styles from './ManualScanButton.module.css';

export const MANUAL_SCAN_CONFIRM =
  'Avviare ora la scansione vulnerabilità?\n\n' +
  'Tutti i bundle attivi vengono rianalizzati; al termine partono snapshot dei finding, ' +
  'Product Triage e notifiche email dei finding nuovi.';

/** Mano aperta (stile outline 24×24, tratto = currentColor). */
function HandIcon() {
  return (
    <svg
      className={styles.icon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 11V6a2 2 0 0 0-4 0v5" />
      <path d="M14 10V4a2 2 0 0 0-4 0v6" />
      <path d="M10 10.5V6a2 2 0 0 0-4 0v8" />
      <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
    </svg>
  );
}

function errorText(error: Error): string {
  if (error instanceof ApiError && error.status === 409) return 'Scansione già in corso';
  return error.message || 'Avvio non riuscito';
}

export function ManualScanButton() {
  const { claims } = useAuth();
  const isAdmin = claims?.roles.includes('admin') ?? false;
  const { data: status } = useManualScanStatus(Boolean(claims));
  const { mutate, isPending, error, reset } = useStartManualScan();
  const [denyVisible, setDenyVisible] = useState(false);

  if (!claims) return null;

  if (status?.activeRun) {
    return (
      <Link
        to="/jobs"
        className={`${styles.button} ${styles.running}`}
        data-testid="manual-scan-running"
        title={`Scansione vulnerabilità in corso (${status.activeRun.state}): apri Scheduled Job`}
      >
        <HandIcon />
        Scansione in corso…
      </Link>
    );
  }

  const unavailable = status?.available === false;

  function onClick() {
    if (!isAdmin) {
      setDenyVisible(true);
      return;
    }
    if (!window.confirm(MANUAL_SCAN_CONFIRM)) return;
    reset();
    mutate();
  }

  return (
    <>
      <button
        type="button"
        className={styles.button}
        onClick={onClick}
        disabled={isPending || unavailable}
        data-testid="manual-scan-button"
        title={
          unavailable
            ? 'Job manual-vuln-scan non disponibile in Prefect'
            : 'Operazione manuale: rianalisi vulnerabilità, poi snapshot, Product Triage e notifiche'
        }
      >
        <HandIcon />
        {isPending ? 'Avvio…' : 'Avvia Scansione Vulnerabilità'}
      </button>
      {error && (
        <span className={styles.error} role="alert" data-testid="manual-scan-error">
          {errorText(error)}
        </span>
      )}
      {denyVisible && (
        <RunDenyPopup sub={claims.sub} roles={claims.roles} onClose={() => setDenyVisible(false)} />
      )}
    </>
  );
}
